import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, CssBaseline, FormControl, Stack, TextField, Typography
} from "@mui/material";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import banner from "../../images/fesr-puglia-banner.png";
import PedalBoard from "../../components/pedal/PedalBoard";
import MapContainer from "../../components/MapContainer";
import AdvancedMarker from "../../components/AdvancedMarker";
import LanguageSelect from "../../components/LanguageSelect";
import { BrandLogo } from "../../components/pages/LoginPage";
import { loadSharedPedalAlert, loadSharedPedalBoard, useGetPedalShare } from "../../services/ContentOpenManager";
import { BRANDS } from "../../branding";
import { PINBIKER_HEADER_HEIGHT } from "./PinbikerHeader";
import SideRail, { RAIL_WIDTH } from "./SideRail";

const RADIUS = "14px";
const MODE_THEFT_ALARM = "2";
const MAP_HEIGHT = { xs: 260, md: 340 };
// Le pagine pubbliche del pedale sono sempre UGO, qualunque sia il marchio del sito.
const BRAND = BRANDS.ugo;
// Rotta pubblica del monitoraggio: generica, non legata al pedale. `/pedale` resta solo come rimando.
export const MONITOR_PATH = "/monitor";
export const monitorUrl = token => `${MONITOR_PATH}/${encodeURIComponent(token)}`;

/**
 * Header white-label delle pagine pubbliche del pedale, come quello della pagina del pinbiker: logo UGO a
 * sinistra, lingua a destra, e a destra della lingua quello che passa la pagina (il proprietario del pedale).
 * Niente marchio Pin Bike: la pagina la apre chi riceve il link.
 */
export function PedalShareHeader({ children }) {
    const { i18n } = useTranslation();
    return (
        <Box component="header" sx={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100, height: PINBIKER_HEADER_HEIGHT,
            bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center",
            px: { xs: 2, md: 3 }, gap: 1,
        }}>
            <BrandLogo brand={BRAND} height={BRAND.logoHeight.header} />
            <Box sx={{ flexGrow: 1 }} />
            <FormControl size="small" sx={{ minWidth: 110, display: { xs: "none", sm: "inline-flex" } }}>
                <LanguageSelect color="inherit" value={i18n.language}
                    onChange={({ target }) => i18n.changeLanguage(target.value)} />
            </FormControl>
            {children}
        </Box>
    );
}

/** Banner dei finanziatori (UE / MiSE / Regione Puglia / PO FESR-FSE), obbligatorio sulle pagine pubbliche. */
export function FundingBanner() {
    const { t } = useTranslation();
    return (
        <Box sx={{ bgcolor: "#fff", borderRadius: RADIUS, p: 1.5, mt: 2, display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <Box component="img" src={banner} alt={t('fundingBannerAlt')}
                sx={{ width: "100%", maxWidth: 720, height: "auto", display: "block" }} />
        </Box>
    );
}

/**
 * Guscio grigio delle pagine pubbliche del pedale: contenuto sotto l'header fisso, spostato se c'e' la barra.
 * Con `fill`, da computer il contenuto occupa esattamente lo schermo (colonna flessibile alta 100vh): la mappa
 * prende lo spazio libero e il banner dei finanziatori resta visibile senza scorrere.
 */
export function PedalShareShell({ header, rail, fill, children }) {
    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f4f5f7" }}>
            {/* le pagine pubbliche non passano dal guscio dell'app: senza, il body tiene gli 8px di margine del
                browser e con la colonna alta 100vh la pagina scorre di 16px, una striscia bianca in fondo */}
            <CssBaseline />
            <PedalShareHeader>{header}</PedalShareHeader>
            {rail}
            <Box component="main" sx={{
                pt: `${PINBIKER_HEADER_HEIGHT + 16}px`, px: { xs: 1.5, md: 3 }, pb: 2,
                ml: rail ? { md: `${RAIL_WIDTH}px` } : 0,
                ...(fill && { display: { md: "flex" }, flexDirection: "column", height: { md: "100vh" }, boxSizing: "border-box" }),
            }}>
                {children}
            </Box>
        </Box>
    );
}

/**
 * Campo per aprire il pannello di un token: nella pagina del codice e, compatto, sopra la mappa per passare a
 * monitorare un altro utente. Gli spazi si tolgono, perche' chi lo detta o lo copia ne aggiunge.
 */
export function TokenForm({ compact, autoFocus }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [code, setCode] = React.useState("");
    const clean = code.replace(/\s+/g, "");
    const open = e => {
        e.preventDefault();
        if (!clean) return;
        history.push(monitorUrl(clean));
        setCode("");
    };
    return (
        <Stack component="form" onSubmit={open} direction={compact ? "row" : { xs: "column", sm: "row" }}
            spacing={compact ? 1 : 1.5} sx={compact ? { width: { xs: "100%", md: 340 } } : undefined}>
            <TextField fullWidth size="small" autoFocus={autoFocus} value={code} onChange={e => setCode(e.target.value)}
                label={compact ? undefined : t('pedalShareEnterCode')}
                placeholder={compact ? t('pedalShareOtherCode') : undefined}
                inputProps={{ autoComplete: "off", spellCheck: false, "aria-label": t('pedalShareOtherCode') }}
                sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: "10px" } }} />
            <Button type="submit" variant="contained" disableElevation disabled={!clean}
                sx={{
                    whiteSpace: "nowrap", borderRadius: "10px", bgcolor: BRAND.color, color: BRAND.contrastText,
                    "&:hover": { bgcolor: BRAND.color, filter: "brightness(0.92)" },
                }}>
                {t('pedalShareOpen')}
            </Button>
        </Stack>
    );
}

/**
 * Pagina pubblica (senza login) del link condivisibile, impaginata come la pagina del pinbiker ma con la
 * sola mappa: pannello del pedale con gli allarmi furto e le loro posizioni. Si aggiorna ogni 60 secondi;
 * a link scaduto o revocato rimanda al codice.
 */
export default function PedalSharePage() {
    const { t } = useTranslation();
    const { token } = useParams();
    const { status, view } = useGetPedalShare(token);

    const rail = <SideRail items={[{ key: "map", label: t('map'), icon: <MapOutlinedIcon /> }]}
        active="map" accent={BRAND.accent} />;

    return (
        <PedalShareShell fill rail={rail} header={view && <OwnerBadge name={view.ownerName} />}>
            {status === "loading" &&
                <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>}
            {status === "error" &&
                <Box sx={{
                    bgcolor: "#fff", borderRadius: RADIUS, p: { xs: 2, md: 4 }, maxWidth: 560, mx: "auto",
                    width: "100%", boxSizing: "border-box",
                }}>
                    <Typography sx={{ mb: 2 }}>{t('pedalShareInvalid')}</Typography>
                    <TokenForm autoFocus />
                </Box>}
            {view && <>
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5} sx={{ mb: 2, flexShrink: 0 }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1.6 }}>
                            {t('pedalShareTitle')}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{view.ownerName}</Typography>
                    </Box>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}
                        sx={{ width: { xs: "100%", md: "auto" } }}>
                        <Chip icon={<ScheduleIcon />} variant="outlined"
                            label={`${t('expiresOn')} ${dayjs(new Date(view.expiresAt)).format("DD/MM/YYYY HH:mm")}`}
                            sx={{ bgcolor: "#fff", borderColor: "#e5e7eb", fontWeight: 600, alignSelf: { xs: "flex-start", md: "center" } }} />
                        <TokenForm compact />
                    </Stack>
                </Stack>
                {String(view.mode) === MODE_THEFT_ALARM &&
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px", flexShrink: 0 }}>{t('pedalTheftAlarm')}</Alert>}
                {/* Da computer il pannello prende lo spazio rimasto fra la riga del nome e il banner. */}
                <Box sx={{ flex: { md: 1 }, minHeight: { md: 360 } }}>
                    <PedalBoard queryKey={["share", token]} loadBoard={loadSharedPedalBoard(token)}
                        loadAlert={loadSharedPedalAlert(token)} empty={<LastPosition view={view} />}
                        height={{ xs: "auto", md: "100%" }} />
                </Box>
            </>}
            <FundingBanner />
        </PedalShareShell>
    );
}

/** Proprietario del pedale nell'header, al posto dell'utente loggato della pagina del pinbiker. */
function OwnerBadge({ name }) {
    const initials = (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
    return (
        <Box sx={{
            display: "flex", alignItems: "center", gap: 1, border: "1px solid #e5e7eb", borderRadius: "10px",
            px: 1, py: 0.5,
        }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: BRAND.color }}>{initials}</Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, display: { xs: "none", sm: "block" } }}>{name}</Typography>
        </Box>
    );
}

/** Senza pannello (server UGO giu' o nessun pedale elencato): l'ultima posizione della vista pubblica, se c'e'. */
function LastPosition({ view }) {
    const { t } = useTranslation();
    if (view.latitude == null || view.longitude == null)
        return <Typography color="text.secondary" sx={{ p: 2 }}>{t('noPedalPosition')}</Typography>;
    const center = { lat: view.latitude, lng: view.longitude };
    return (
        <Box>
            <Box sx={{ height: MAP_HEIGHT, borderRadius: RADIUS, overflow: "hidden" }}>
                <MapContainer center={center} zoom={16}>
                    <AdvancedMarker position={center} />
                </MapContainer>
            </Box>
            {view.timestamp &&
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('lastUpdate')} {dayjs(new Date(view.timestamp)).format("DD/MM/YYYY HH:mm")}
                </Typography>}
        </Box>
    );
}
